import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './AuthProvider';

import type { ReactNode } from 'react';

/**
 * The authenticated branch of the route tree.
 *
 * **This is not the security boundary.** It decides what to render; the API's guard
 * decides what a caller may have, and it does so against a token it verified. A
 * determined non-admin who edits the bundle gets a 403 from the server, which is the
 * assertion AUTH-06's proof captures.
 *
 * What this does buy is that people are not shown doors that will not open.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Nothing is decided until the stored session has been read. Redirecting during
  // that gap is what turns a page refresh into an unexplained sign-out.
  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // `state.from` so sign-in can return the person to the page they asked for,
    // rather than dropping them on the dashboard having forgotten why they came.
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
